// © 2013 - 2022 Tinder, Inc., ALL RIGHTS RESERVED

import React, { createContext } from 'react';

import { Theme, getThemeClassName } from '@tinderapp/ui-core';

import { DEFAULT_THEMES, DEFAULT_THEME_ID } from './constants';

export const UiEngineThemesContext = createContext<Record<string, Theme>>(DEFAULT_THEMES);
export const UiEngineThemeIdContext = createContext<string>(DEFAULT_THEME_ID);

/**
 * UiEngineProvider
 * ================
 *
 * This context is used to provide the global list of themes and inject their styles.
 */

export const UiEngineProvider: React.FunctionComponent<{
  children: React.ReactNode;
  themeId?: string;
  themes?: Record<string, Theme>;
}> = ({ themeId, themes = DEFAULT_THEMES, children }) => {
  return (
    <UiEngineThemesContext.Provider value={themes}>
      {themeId ? (
        <ThemeIdProvider tagName={null} themeId={themeId}>
          {children}
        </ThemeIdProvider>
      ) : (
        children
      )}
    </UiEngineThemesContext.Provider>
  );
};

/**
 * ThemeIdProvider
 * ===============
 *
 * This context sets the current theme ID for downstream components.
 */

export const ThemeIdProvider: React.FunctionComponent<{
  children: React.ReactNode;
  tagName?: React.ComponentType<{ className: string }> | null;
  themeId: string;
}> = ({ tagName = 'div', themeId, children, ...restProps }) => {
  const className = getThemeClassName(themeId);
  return (
    <UiEngineThemeIdContext.Provider value={themeId}>
      {tagName ? React.createElement(tagName, { className, ...restProps }, children) : children}
    </UiEngineThemeIdContext.Provider>
  );
};
